/**
 * LessonQuizDialog — admin/SME/instructor authors a per-lesson practice quiz.
 *
 * Mirrors QuizDialog.jsx but scoped to a single lesson (not a chapter).
 * Provides the same "AI: Draft 20 Qs" flow — scans the lesson body + any
 * attached PDFs (OCR fallback for scans) and drafts 20 MCQs. Author
 * reviews/edits before saving.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, CheckCircle2, Sparkles, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAiJobs } from "@/lib/ai-jobs";

const BLANK_Q = () => ({ text: "", options: ["", "", "", ""], correct_index: 0 });

export default function LessonQuizDialog({ open, onOpenChange, lessonId, lessonTitle, onSaved }) {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [genStats, setGenStats] = useState(null);
    const { trackJob } = useAiJobs();

    useEffect(() => {
        if (!open || !lessonId) return;
        setLoading(true);
        api.get(`/lessons/${lessonId}/quiz`)
            .then((r) => {
                const qs = (r.data?.questions || []).map((q) => ({
                    text: q.text || "",
                    options: q.options?.length === 4 ? q.options : ["", "", "", ""],
                    correct_index: q.correct_index ?? 0,
                }));
                setQuestions(qs.length ? qs : [BLANK_Q()]);
            })
            .catch(() => setQuestions([BLANK_Q()]))
            .finally(() => setLoading(false));
    }, [open, lessonId]);

    const updateQ = (idx, patch) => {
        setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
    };
    const updateOption = (qIdx, optIdx, value) => {
        setQuestions((qs) =>
            qs.map((q, i) =>
                i === qIdx
                    ? { ...q, options: q.options.map((o, j) => (j === optIdx ? value : o)) }
                    : q,
            ),
        );
    };
    const addQ = () => setQuestions((qs) => [...qs, BLANK_Q()]);
    const removeQ = (idx) => setQuestions((qs) => qs.filter((_, i) => i !== idx));

    const autoGenerate = async () => {
        if (!lessonId) return;
        setGenerating(true);
        setGenStats(null);
        try {
            const { data: kick } = await api.post(
                `/lessons/${lessonId}/quiz/auto-generate`,
            );
            const jobId = kick?.job_id;
            if (!jobId) {
                toast.error("Could not start AI generation. Try again.");
                setGenerating(false);
                return;
            }
            trackJob({
                kind: "lesson-quiz",
                jobId,
                lessonId,
                lessonTitle,
                onDone: () => onSaved?.(),
            });
            toast.success("AI drafting lesson quiz — track progress in the AI jobs tray. Reopen this lesson quiz when it's done to review the questions.");
            onOpenChange(false);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "AI quiz generation failed.",
            );
        } finally {
            setGenerating(false);
        }
    };

    const save = async () => {
        for (const [i, q] of questions.entries()) {
            if (!q.text.trim()) {
                toast.error(`Question ${i + 1} has no text`);
                return;
            }
            if (q.options.some((o) => !o.trim())) {
                toast.error(`Question ${i + 1} has empty options`);
                return;
            }
        }
        setSaving(true);
        try {
            await api.put(`/lessons/${lessonId}/quiz`, { questions });
            toast.success("Lesson quiz saved");
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const removeAll = async () => {
        if (!window.confirm("Delete this lesson's quiz? Past attempts are preserved.")) return;
        try {
            await api.delete(`/lessons/${lessonId}/quiz`);
            toast.success("Lesson quiz removed");
            setQuestions([BLANK_Q()]);
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="lesson-quiz-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Lesson quiz</DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        {lessonTitle ? <>Practice quiz for <strong>{lessonTitle}</strong>. </> : null}
                        Students score 80% (16/20 on a 20-question quiz) to pass. They
                        cannot advance to the next lesson until they hit that bar. The
                        answer key below is only ever shown to instructors and admins —
                        students see questions &amp; options without it.
                    </p>
                </DialogHeader>

                <div
                    className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 flex items-start gap-3"
                    data-testid="lesson-quiz-auto-generate-card"
                >
                    <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-foreground">
                            Auto-generate quiz from this lesson
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            AI scans the lesson body plus any PDF attachments on this
                            lesson (with OCR fallback for scanned pages) and drafts 20
                            multiple-choice questions with the answer key. Replaces
                            anything you have below — review before saving.
                        </p>
                        {genStats ? (
                            <p
                                className="text-[11px] text-primary/80 mt-1"
                                data-testid="lesson-quiz-gen-stats"
                            >
                                Last run: {genStats.pdf_attachments_scanned} PDF
                                {genStats.pdf_attachments_scanned === 1 ? "" : "s"}
                                {genStats.ocr_used ? " · OCR used" : ""} ·{" "}
                                {Math.round((genStats.chars_scanned || 0) / 1000)}k chars
                            </p>
                        ) : null}
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        onClick={autoGenerate}
                        disabled={generating || loading || saving}
                        className="ai-shimmer gap-1.5 flex-shrink-0"
                        data-testid="lesson-quiz-auto-generate-btn"
                    >
                        {generating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                        )}
                        <span className="text-xs font-semibold">
                            {generating ? "Drafting…" : "AI: Draft 20 Qs"}
                        </span>
                    </Button>
                </div>

                <div
                    className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-2"
                    data-testid="lesson-quiz-answer-bank-label"
                >
                    <Lock className="h-3 w-3" />
                    Answer bank — instructor / admin only
                </div>

                {loading ? (
                    <div className="py-12 text-center text-muted-foreground">Loading…</div>
                ) : (
                    <div className="space-y-5">
                        {questions.map((q, qIdx) => (
                            <div
                                key={qIdx}
                                className="border border-border rounded-sm p-4 bg-secondary/20"
                                data-testid={`lesson-quiz-q-${qIdx}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="font-semibold">Question {qIdx + 1}</Label>
                                    {questions.length > 1 && (
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => removeQ(qIdx)}
                                            data-testid={`lesson-quiz-q-del-${qIdx}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    )}
                                </div>
                                <Textarea
                                    rows={2}
                                    value={q.text}
                                    onChange={(e) => updateQ(qIdx, { text: e.target.value })}
                                    placeholder="Question text"
                                    data-testid={`lesson-quiz-q-text-${qIdx}`}
                                />
                                <div className="mt-3 space-y-2">
                                    {q.options.map((opt, optIdx) => (
                                        <div key={optIdx} className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => updateQ(qIdx, { correct_index: optIdx })}
                                                className="flex-shrink-0"
                                                data-testid={`lesson-quiz-correct-${qIdx}-${optIdx}`}
                                                aria-label={`Mark option ${optIdx + 1} correct`}
                                            >
                                                <CheckCircle2
                                                    className={`h-5 w-5 ${
                                                        q.correct_index === optIdx
                                                            ? "text-accent fill-accent/20"
                                                            : "text-muted-foreground"
                                                    }`}
                                                />
                                            </button>
                                            <Input
                                                value={opt}
                                                onChange={(e) => updateOption(qIdx, optIdx, e.target.value)}
                                                placeholder={`Option ${optIdx + 1}`}
                                                data-testid={`lesson-quiz-opt-${qIdx}-${optIdx}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        <Button
                            variant="outline"
                            onClick={addQ}
                            className="w-full"
                            data-testid="lesson-quiz-add-q-btn"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add question
                        </Button>
                    </div>
                )}

                <DialogFooter className="flex items-center justify-between gap-2">
                    <div>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={removeAll}
                            className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                            data-testid="lesson-quiz-remove-btn"
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove quiz
                        </Button>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={save}
                            disabled={saving || loading}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="lesson-quiz-save-btn"
                        >
                            {saving ? "Saving…" : "Save quiz"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
